import { 
  Box, 
  Button, 
  Checkbox, 
  FormControl, 
  FormControlLabel, 
  FormGroup, 
  FormHelperText, 
  MenuItem, 
  Modal, 
  Select, 
  Stack, 
  TextField, 
  Typography 
} from "@mui/material";
import { ClientData, Permission, UserData, UserDataError } from "../../types";
import { useEffect, useState } from "react";
import { useGlobalAtoms } from "../../atoms/globalAtoms";
import { useAPI } from "../../hooks/useAPI";
import { useAuth } from "../../assets/contexts/AuthContext";
import { useConfirmDialog } from "../../hooks/useConfirmDialog";
import { ConfirmDialog } from "../../components/global/ConfirmDialog";

interface UserModalProps {
  open: boolean;
  handleClose:() => void;
  handleSave:() => void;
  user: UserData;
  clients: ClientData[];
}

export const UserModal = ({ open, handleClose, handleSave, user, clients }: UserModalProps) => {
  const [mUser, setMUser] = useState<UserData>({});
  const [error, setError] = useState<UserDataError>({});
  const { allClientPermissons } = useGlobalAtoms();
  const { fetchData } = useAPI();
  const { user: authUser } = useAuth();
  const { isOpen, config, openDialog, closeDialog } = useConfirmDialog();

  useEffect(() => {
    setMUser({ ...user, permissions: [...(user.permissions ?? [])] });
  }, [user]);

  const onChangePermission = (permission: Permission) => {
    const newUser = { ...mUser, permissions: [...(mUser.permissions ?? [])] };
    const userPermissionIndex = newUser.permissions.findIndex(item => item.id == permission.id);
    if (userPermissionIndex >= 0) {
      newUser.permissions = newUser.permissions.filter(item => item.id != permission.id);
    } else {
      newUser.permissions = [ ...newUser.permissions, {...permission}];
    }
    setMUser(newUser);
  }

  const onSaveUser = async (user: UserData) => {
    if (!authUser?.is_super_admin && authUser?.client_id) {
      user.client_id = authUser.client_id;
    }

    const {errors, data} = await fetchData('/users/save',  user, 'POST');
    if (errors) {
      setError(errors);
    } else {
      handleSave();
    }
  }

  const onDeleteUser = async (user: UserData) => {
    const {errors, data} = await fetchData('/users/delete',  { id: user.id }, 'POST');
    if (errors) {
      setError(errors);
    } else {
      handleSave();
    }
  }

  return (
    <Modal
      open={open}
      // onClose={handleClose}
      aria-labelledby="modal-title"
      aria-describedby="modal-description"
    >
      <Box sx={{ 
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '80%',
        maxWidth: '750px',
        bgcolor: 'background.paper',
        border: '1px solid #909894',
        boxShadow: '0px 4px 4px 0px #00000040',
        borderRadius:'16px',
        overflow: 'auto',
        maxHeight: '90%',
        p: 4,
       }}>
        <Typography id="modal-title" variant="h4" component="h2" fontWeight="600">
          { user.id ? "Edit User" : "Add User" }
        </Typography>
        <Stack mt={4} spacing={4}>
          <FormGroup>
            <Typography fontWeight="600">First Name</Typography>
            <TextField
              variant="outlined"
              value={mUser.first_name}
              error={error.first_name ? true : false}
              helperText={error?.first_name?.[0] ?? ''}
              onChange={(e) => setMUser({ ...mUser, first_name: e.target.value })}
              sx={{ width: '100%' }}
            />
          </FormGroup>
          <FormGroup>
            <Typography fontWeight="600">Last Name</Typography>
            <TextField
              variant="outlined"
              value={mUser.last_name}
              error={error.last_name ? true : false}
              helperText={error?.last_name?.[0] ?? ''}
              onChange={(e) => setMUser({ ...mUser, last_name: e.target.value })}
              sx={{ width: '100%' }}
            />
          </FormGroup>
          <FormGroup>
            <Typography fontWeight="600">Email</Typography>
            <TextField
              variant="outlined"
              value={mUser.email}
              error={error.email ? true : false}
              helperText={error?.email?.[0] ?? ''}
              type="email"
              onChange={(e) => setMUser({ ...mUser, email: e.target.value })}
              sx={{ width: '100%' }}
            />
          </FormGroup>
          {
            authUser?.is_super_admin ? (
              <>
                <FormControlLabel 
                  sx={{ width: '40%' }} 
                  value={1}
                  control={<Checkbox onChange={(e) => setMUser({ ...mUser, is_super_admin: e.target.checked, client_id: undefined })} />} 
                  label={"Super Admin User"}
                  checked={mUser?.is_super_admin ? true : false}
                  />
                <FormGroup>
                  <FormControl error={error.client_id ? true : false} disabled={mUser.is_super_admin} >
                    <Typography fontWeight="600">Client</Typography>
                    <Select
                      variant="outlined"
                      value={mUser.client_id ?? ''}
                      onChange={(e) => setMUser({ ...mUser, client_id: e.target.value })}
                      sx={{ width: '100%' }}
                    >
                      {
                        clients.map((item: any, index: number) => (
                          <MenuItem key={index} value={item.id}>{item.name}</MenuItem>
                        ))
                      }
                    </Select>
                    {
                      error.client_id ? (
                        <FormHelperText color="#ff1744" style={{ marginBottom: "-20px" }}>
                          {error?.client_id?.[0] ?? ''}
                        </FormHelperText>
                      ) : null
                    }
                  </FormControl>
                </FormGroup>
              </>
            ) : null
          }
          <FormGroup>
            <Typography fontWeight="600">Permissions</Typography>
            <Stack flexWrap='wrap' direction='row' >
              {
                allClientPermissons.map((item: any, index: number) => (
                  <FormControlLabel 
                    key={index}
                    sx={{ width: '40%' }} 
                    control={<Checkbox />} 
                    value={item.id} 
                    label={item.name}
                    checked={(mUser?.permissions && mUser?.permissions?.findIndex(permission => permission.id == item.id) >= 0) ? true : false} 
                    onChange={() => onChangePermission(item)}
                  />
                ))
              }
            </Stack>
          </FormGroup>
          <FormGroup>
            <Stack direction='row' spacing={0} sx={{ width: '100%' }}>
              <Box sx={{ width:  '40%' }}>
                <FormControlLabel 
                  sx={{ width: '100%' }} 
                  control={<Checkbox />} 
                  value={true}
                  label="Set User Password" 
                  checked={mUser.set_password ? true : false}
                  onChange={(e, status) => setMUser({ ...mUser, set_password: status })}
                />
              </Box>
              <Stack flexWrap='wrap' direction='row' >
                <FormControlLabel 
                  sx={{ width: '100%' }} 
                  control={<Checkbox />} 
                  value={true}
                  label="User Active" 
                  checked={mUser.status ? true : false}
                  disabled={user.id == mUser.id}
                  onChange={(e, status) => setMUser({ ...mUser, status: status })}
                />
              </Stack>
            </Stack>
          </FormGroup>
          {
            mUser.set_password ? (
              <>
                <FormGroup>
                  <Typography fontWeight="600">Password</Typography>
                  <TextField
                    variant="outlined"
                    value={mUser.password}
                    error={error.password ? true : false}
                    helperText={error?.password?.[0] ?? ''}
                    type="password"
                    onChange={(e) => setMUser({ ...mUser, password: e.target.value })}
                    sx={{ width: '100%' }}
                  />
                </FormGroup>
                <FormGroup>
                  <Typography fontWeight="600">Repeat Password</Typography>
                  <TextField
                    variant="outlined"
                    value={mUser.repeat_password}
                    error={error.repeat_password ? true : false}
                    helperText={error?.repeat_password?.[0] ?? ''}
                    type="password"
                    onChange={(e) => setMUser({ ...mUser, repeat_password: e.target.value })}
                    sx={{ width: '100%' }}
                  />
                </FormGroup>
              </>
            ) : null
          }
          <FormGroup>
            <Stack direction='row' spacing={2}>
              <Button variant="contained" size="large" onClick={() => onSaveUser(mUser)}>
                Save User
              </Button>
              {
                user.id && (
                  <Button variant="outlined" size="large" color="error" disabled={user.id == mUser.id} onClick={() => 
                    openDialog(
                      "Confirm Deletion",
                      `Are you sure you want to delete the client "${mUser.first_name} ${mUser.last_name}"?`,
                      () => onDeleteUser(mUser)
                    )
                  }>
                    Delete User
                  </Button>
                )
              }
              <Button variant="text" size="large" onClick={handleClose}>
                Cancel
              </Button>
            </Stack>
          </FormGroup>
        </Stack>
        <ConfirmDialog
          open={isOpen}
          title={config.title}
          description={config.description}
          onConfirm={config.onConfirm}
          onClose={closeDialog}
        />
      </Box>
    </Modal>
  )
}